import { getOfflinePosSnapshot } from "../api/offlinePos";
import { concreteIdentityKey } from "../utils/productVariants";
import {
  OFFLINE_STORES,
  cleanupOldOfflineReadSnapshots,
  clearOfflineReadSnapshot,
  commitOfflineReadSnapshot,
  getOfflineRecord,
  getOfflineRecordsByScope,
  getPendingOfflineSoldQuantities,
} from "./db";
import {
  getOfflineDevice,
  getOfflinePosSession,
  isOfflineGrantUsable,
  offlineScope,
} from "./session";

export const OFFLINE_SNAPSHOT_SCHEMA_VERSION = 2;

const snapshotRecordCache = new Map();

function clearSnapshotRecordCache(scope) {
  if (!scope) return;
  for (const key of snapshotRecordCache.keys()) {
    if (key.startsWith(`${scope}|`)) snapshotRecordCache.delete(key);
  }
}

function snapshotMetaKey(scope) {
  return `snapshot:${scope}`;
}

function normalizeSearch(value) {
  return String(value || "").trim().toLocaleLowerCase();
}

function matchesAllTerms(values, query) {
  const terms = normalizeSearch(query).split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const haystack = values.filter(Boolean).join(" ").toLocaleLowerCase();
  return terms.every((term) => haystack.includes(term));
}

function productRecordKey(scope, publicId) {
  return `${scope}:product:${publicId}`;
}

function identityRecordKey(scope, productPublicId, variantPublicId) {
  return `${scope}:identity:${concreteIdentityKey(productPublicId, variantPublicId)}`;
}

function inventoryRecordKey(scope, productPublicId, variantPublicId) {
  return `${scope}:inventory:${concreteIdentityKey(productPublicId, variantPublicId)}`;
}

function barcodeRecordKey(scope, barcode) {
  return `${scope}:barcode:${barcode}`;
}

function normalizeOfflineBarcode(value) {
  const normalized = String(value || "").replace(/^[ \t\r\n]+|[ \t\r\n]+$/g, "");
  if (!normalized) throw new Error("Barcode cannot be empty.");
  if (normalized.length > 160) throw new Error("Barcode is too long.");
  if ([...normalized].some((character) => {
    const code = character.charCodeAt(0);
    return code < 32 || code === 127;
  })) {
    throw new Error("Barcode contains unsupported control characters.");
  }
  return normalized;
}

function requireMatchingSnapshot(
  data,
  workspacePublicId,
  storePublicId,
  devicePublicId,
) {
  if (Number(data?.schema_version) !== OFFLINE_SNAPSHOT_SCHEMA_VERSION) {
    throw new Error("Offline POS snapshot schema is not supported by this ABOS version.");
  }
  if (
    data?.workspace_public_id !== workspacePublicId
    || data?.store?.public_id !== storePublicId
    || data?.device_public_id !== devicePublicId
    || !data?.snapshot_id
  ) {
    throw new Error("Offline POS snapshot identity does not match the current store device.");
  }
  if (
    !Array.isArray(data.products)
    || !Array.isArray(data.catalog_items)
    || !Array.isArray(data.inventory)
    || !Array.isArray(data.barcodes)
  ) {
    throw new Error("Offline POS snapshot is incomplete.");
  }
}

export async function saveOfflinePosSnapshot({
  workspacePublicId,
  storePublicId,
  devicePublicId,
  snapshot,
}) {
  const scope = offlineScope(workspacePublicId, storePublicId);
  if (!scope) {
    throw new Error("Workspace and store are required for Offline POS snapshot storage.");
  }
  requireMatchingSnapshot(snapshot, workspacePublicId, storePublicId, devicePublicId);

  const snapshotId = snapshot.snapshot_id;
  const pendingByIdentity = await getPendingOfflineSoldQuantities(scope);
  const catalogRecords = [
    ...snapshot.products.map((product) => ({
      ...product,
      key: productRecordKey(scope, product.public_id),
      scope,
      snapshot_id: snapshotId,
      kind: "product",
    })),
    ...snapshot.catalog_items.map((item) => ({
      ...item,
      key: identityRecordKey(
        scope,
        item.product_public_id,
        item.product_variant_public_id,
      ),
      scope,
      snapshot_id: snapshotId,
      kind: "identity",
      identity_key: concreteIdentityKey(
        item.product_public_id,
        item.product_variant_public_id,
      ),
    })),
  ];
  const barcodeRecords = snapshot.barcodes.map((barcode) => ({
    ...barcode,
    key: barcodeRecordKey(scope, barcode.barcode),
    scope,
    snapshot_id: snapshotId,
  }));
  const inventoryRecords = snapshot.inventory.map((item) => ({
    ...item,
    key: inventoryRecordKey(
      scope,
      item.product_public_id,
      item.product_variant_public_id,
    ),
    scope,
    snapshot_id: snapshotId,
    identity_key: concreteIdentityKey(
      item.product_public_id,
      item.product_variant_public_id,
    ),
    pending_offline_sold_quantity: pendingByIdentity.get(
      concreteIdentityKey(item.product_public_id, item.product_variant_public_id),
    ) || 0,
  }));
  const metadata = {
    scope,
    schema_version: OFFLINE_SNAPSHOT_SCHEMA_VERSION,
    snapshot_id: snapshotId,
    generated_at: snapshot.generated_at,
    cached_at: new Date().toISOString(),
    workspace_public_id: workspacePublicId,
    store_public_id: storePublicId,
    device_public_id: devicePublicId,
    store: snapshot.store,
    counts: snapshot.counts || {
      products: snapshot.products.length,
      catalog_items: snapshot.catalog_items.length,
      inventory: snapshot.inventory.length,
      barcodes: snapshot.barcodes.length,
    },
  };

  await commitOfflineReadSnapshot({
    scope,
    metadata,
    catalogRecords,
    barcodeRecords,
    inventoryRecords,
  });
  clearSnapshotRecordCache(scope);
  try {
    await cleanupOldOfflineReadSnapshots(scope, snapshotId);
  } catch {
    // The committed snapshot remains authoritative; stale-row cleanup is best effort.
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("offline:snapshot-updated", { detail: metadata }));
  }
  return metadata;
}

export async function refreshOfflinePosSnapshot(
  workspacePublicId,
  storePublicId,
  options = {},
) {
  const [device, session] = await Promise.all([
    getOfflineDevice(workspacePublicId, storePublicId),
    getOfflinePosSession(workspacePublicId, storePublicId),
  ]);
  if (!device?.device_public_id || !device?.device_secret || device.status !== "active") {
    throw new Error("This browser is not enrolled as an Offline POS device.");
  }
  if (
    !session
    || session.device_public_id !== device.device_public_id
    || !isOfflineGrantUsable(session)
  ) {
    throw new Error("Offline POS authorization must be refreshed before catalog sync.");
  }

  const response = await getOfflinePosSnapshot(
    device.device_public_id,
    device.device_secret,
    session.grant,
    options,
  );
  return saveOfflinePosSnapshot({
    workspacePublicId,
    storePublicId,
    devicePublicId: device.device_public_id,
    snapshot: response.data,
  });
}

export async function getOfflineSnapshotStatusMetadata(workspacePublicId, storePublicId) {
  const scope = offlineScope(workspacePublicId, storePublicId);
  if (!scope) return null;
  const metadata = await getOfflineRecord(OFFLINE_STORES.META, snapshotMetaKey(scope));
  if (
    !metadata
    || metadata.scope !== scope
    || Number(metadata.schema_version) !== OFFLINE_SNAPSHOT_SCHEMA_VERSION
    || !metadata.snapshot_id
    || metadata.workspace_public_id !== workspacePublicId
    || metadata.store_public_id !== storePublicId
  ) {
    return null;
  }
  // Status/diagnostic metadata is safe to show even when a device was revoked
  // or its grant expired. Business reads still use getOfflineSnapshotMetadata,
  // which enforces the full trusted device + grant + identity contract.
  return metadata;
}

export async function getOfflineSnapshotMetadata(workspacePublicId, storePublicId) {
  const scope = offlineScope(workspacePublicId, storePublicId);
  if (!scope) return null;

  const [metadata, device, session] = await Promise.all([
    getOfflineRecord(OFFLINE_STORES.META, snapshotMetaKey(scope)),
    getOfflineDevice(workspacePublicId, storePublicId),
    getOfflinePosSession(workspacePublicId, storePublicId),
  ]);
  const identity = session?.identity || {};
  const trusted = Boolean(
    metadata
    && metadata.scope === scope
    && Number(metadata.schema_version) === OFFLINE_SNAPSHOT_SCHEMA_VERSION
    && metadata.snapshot_id
    && device?.status === "active"
    && metadata.device_public_id === device.device_public_id
    && session?.device_public_id === device.device_public_id
    && isOfflineGrantUsable(session)
    && identity.workspace?.public_id === workspacePublicId
    && identity.store?.public_id === storePublicId
    && identity.user?.public_id
  );
  return trusted ? metadata : null;
}

async function loadSnapshotRecords(storeName, workspacePublicId, storePublicId) {
  const scope = offlineScope(workspacePublicId, storePublicId);
  const metadata = await getOfflineSnapshotMetadata(workspacePublicId, storePublicId);
  if (!scope || !metadata) return { metadata: null, records: [] };

  const cacheKey = `${scope}|${metadata.snapshot_id}|${storeName}`;
  let recordsPromise = snapshotRecordCache.get(cacheKey);
  if (!recordsPromise) {
    recordsPromise = getOfflineRecordsByScope(storeName, scope)
      .then((records) => records.filter(
        (record) => record.snapshot_id === metadata.snapshot_id,
      ))
      .catch((error) => {
        snapshotRecordCache.delete(cacheKey);
        throw error;
      });
    snapshotRecordCache.set(cacheKey, recordsPromise);
  }

  return { metadata, records: await recordsPromise };
}

export async function getOfflineCatalogFilterValues({ workspacePublicId, storePublicId, field, query = "" }) {
  if (field !== "brand" && field !== "category") return [];
  const { metadata, records } = await loadSnapshotRecords(
    OFFLINE_STORES.CATALOG_ITEMS, workspacePublicId, storePublicId,
  );
  if (!metadata) return [];
  const needle = query.trim().toLowerCase();
  return [...new Set(records
    .filter((record) => record.kind === "product")
    .map((record) => String(record[field] || "").trim())
    .filter((value) => value && value.toLowerCase().includes(needle)))]
    .sort((left, right) => left.localeCompare(right))
    .slice(0, 50);
}

export async function getOfflineProductsPage({
  workspacePublicId,
  storePublicId,
  query = "",
  brand = "",
  category = "",
  page = 1,
  pageSize = 24,
}) {
  const { metadata, records } = await loadSnapshotRecords(
    OFFLINE_STORES.CATALOG_ITEMS,
    workspacePublicId,
    storePublicId,
  );
  if (!metadata) return { items: [], total: 0, metadata: null };

  const products = records
    .filter((record) => record.kind === "product")
    .filter((product) => !brand || String(product.brand || "").trim().toLowerCase() === brand.trim().toLowerCase())
    .filter((product) => !category || String(product.category || "").trim().toLowerCase() === category.trim().toLowerCase())
    .filter((product) => matchesAllTerms([
      product.name,
      product.brand,
      product.category,
      product.external_id,
    ], query))
    .sort((left, right) => (
      left.name.localeCompare(right.name)
      || left.public_id.localeCompare(right.public_id)
    ));

  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = Math.max(1, Number(pageSize) || 24);
  const start = (safePage - 1) * safeSize;
  return {
    metadata,
    total: products.length,
    items: products.slice(start, start + safeSize).map((product) => ({
      public_id: product.public_id,
      name: product.name,
      brand: product.brand,
      category: product.category,
      price: product.price,
      minimum_price: null,
      cost_price: null,
      description: null,
      has_variants: Boolean(product.has_variants),
      track_inventory: product.track_inventory !== false,
      external_id: product.external_id || null,
      source: product.source || "snapshot",
      image: null,
      created_at: product.created_at,
      offline_snapshot: true,
    })),
  };
}

export async function getOfflineInventoryPage({
  workspacePublicId,
  storePublicId,
  query = "",
  page = 1,
  pageSize = 24,
  status = null,
  brand = "",
  category = "",
}) {
  const [catalog, inventory] = await Promise.all([
    loadSnapshotRecords(OFFLINE_STORES.CATALOG_ITEMS, workspacePublicId, storePublicId),
    loadSnapshotRecords(OFFLINE_STORES.INVENTORY, workspacePublicId, storePublicId),
  ]);
  const metadata = inventory.metadata || catalog.metadata;
  if (!metadata || catalog.metadata?.snapshot_id !== metadata.snapshot_id) {
    return { items: [], total: 0, lowStockTotal: 0, metadata: null };
  }

  const products = new Map(
    catalog.records
      .filter((record) => record.kind === "product")
      .map((record) => [record.public_id, record]),
  );
  const identities = new Map(
    catalog.records
      .filter((record) => record.kind === "identity")
      .map((record) => [record.identity_key, record]),
  );

  const allRows = inventory.records
    .filter((row) => products.get(row.product_public_id)?.track_inventory !== false)
    .filter((row) => !brand || String(products.get(row.product_public_id)?.brand || "").trim().toLowerCase() === brand.trim().toLowerCase())
    .filter((row) => !category || String(products.get(row.product_public_id)?.category || "").trim().toLowerCase() === category.trim().toLowerCase())
    .map((row) => {
      const product = products.get(row.product_public_id);
      const identity = identities.get(row.identity_key);
      if (!product || !identity) return null;
      const minimumStock = Number(row.minimum_stock || 0);
      const availableQuantity = Math.max(
        0,
        Number(row.available_quantity || 0)
          - Number(row.pending_offline_sold_quantity || 0),
      );
      return {
        public_id: row.public_id,
        quantity: Number(row.quantity || 0),
        reserved_quantity: Number(row.reserved_quantity || 0),
        available_quantity: availableQuantity,
        minimum_stock: minimumStock,
        location: row.location || metadata.store?.name || "Store",
        source: row.source || "snapshot",
        is_low_stock: availableQuantity <= minimumStock,
        product: {
          public_id: product.public_id,
          name: product.name,
          brand: product.brand,
          external_id: product.external_id || null,
          sku: product.external_id || null,
        },
        variant: row.product_variant_public_id ? {
          public_id: row.product_variant_public_id,
          label: identity.variant_label || "Variant",
          options: identity.variant_options || {},
          sku: identity.sku || null,
        } : null,
        store: {
          public_id: metadata.store?.public_id || storePublicId,
          name: metadata.store?.name || "Store",
          code: metadata.store?.code || "store",
        },
        _search_values: [
          product.name,
          product.brand,
          product.category,
          product.external_id,
          identity.variant_label,
          identity.sku,
          row.location,
          row.source,
        ],
      };
    })
    .filter(Boolean);

  const lowStockTotal = allRows.filter((row) => row.is_low_stock).length;
  const filtered = allRows
    .filter((row) => status !== "low-stock" || row.is_low_stock)
    .filter((row) => matchesAllTerms(row._search_values, query))
    .sort((left, right) => (
      left.product.name.localeCompare(right.product.name)
      || String(left.variant?.label || "").localeCompare(String(right.variant?.label || ""))
      || left.public_id.localeCompare(right.public_id)
    ));

  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = Math.max(1, Number(pageSize) || 24);
  const start = (safePage - 1) * safeSize;
  return {
    metadata,
    total: filtered.length,
    lowStockTotal,
    items: filtered.slice(start, start + safeSize).map(({ _search_values, ...row }) => row),
  };
}

export async function searchOfflinePosProducts({
  workspacePublicId,
  storePublicId,
  query,
  limit = 24,
}) {
  const [catalog, inventory] = await Promise.all([
    loadSnapshotRecords(OFFLINE_STORES.CATALOG_ITEMS, workspacePublicId, storePublicId),
    loadSnapshotRecords(OFFLINE_STORES.INVENTORY, workspacePublicId, storePublicId),
  ]);
  if (!catalog.metadata || catalog.metadata.snapshot_id !== inventory.metadata?.snapshot_id) {
    return { items: [], metadata: null };
  }

  const inventoryByIdentity = new Map(
    inventory.records.map((record) => [record.identity_key, record]),
  );
  const items = catalog.records
    .filter((record) => record.kind === "identity")
    .filter((item) => item.variant_active !== false)
    .filter((item) => !(item.product_has_variants && !item.product_variant_public_id))
    .map((item) => ({ item, inventory: inventoryByIdentity.get(item.identity_key) }))
    .filter(({ item, inventory: row }) => item.track_inventory === false || Boolean(row))
    .filter(({ item }) => matchesAllTerms([
      item.product_name,
      item.brand,
      item.category,
      item.sku,
      item.variant_label,
      ...Object.values(item.variant_options || {}),
    ], query))
    .slice(0, Math.max(1, Number(limit) || 24))
    .map(({ item, inventory: row }) => {
      const tracked = item.track_inventory !== false;
      const pending = Number(row?.pending_offline_sold_quantity || 0);
      const available = tracked
        ? Math.max(0, Number(row?.available_quantity || 0) - pending)
        : null;
      return {
        product_public_id: item.product_public_id,
        product_variant_public_id: item.product_variant_public_id || null,
        variant_label: item.variant_label || null,
        variant_options: item.variant_options || null,
        product_name: item.product_name,
        brand: item.brand,
        category: item.category,
        sku: item.sku || null,
        price: item.price,
        sale_claim: item.sale_claim,
        track_inventory: tracked,
        physical_quantity: tracked ? Number(row?.quantity || 0) : null,
        reserved_quantity: tracked ? Number(row?.reserved_quantity || 0) : null,
        available_quantity: available,
        in_stock: !tracked || available > 0,
        offline_snapshot: true,
      };
    });

  return { items, metadata: catalog.metadata };
}

export async function lookupOfflinePosBarcode({
  workspacePublicId,
  storePublicId,
  barcode,
}) {
  const normalizedBarcode = normalizeOfflineBarcode(barcode);

  const scope = offlineScope(workspacePublicId, storePublicId);
  const metadata = await getOfflineSnapshotMetadata(workspacePublicId, storePublicId);
  if (!scope || !metadata) throw new Error("No Offline POS catalog snapshot is available.");
  const barcodeRecord = await getOfflineRecord(
    OFFLINE_STORES.BARCODES,
    barcodeRecordKey(scope, normalizedBarcode),
  );
  if (!barcodeRecord || barcodeRecord.snapshot_id !== metadata.snapshot_id) {
    const error = new Error("Barcode was not found in the last Offline POS snapshot.");
    error.code = "OFFLINE_BARCODE_NOT_FOUND";
    throw error;
  }
  if (barcodeRecord.variant_required) {
    const error = new Error(
      "This barcode belongs to a variant product. Scan a barcode assigned to a concrete variant.",
    );
    error.code = "OFFLINE_VARIANT_REQUIRED";
    throw error;
  }
  if (barcodeRecord.variant_active === false) {
    const error = new Error("This barcode belongs to an inactive product variant.");
    error.code = "OFFLINE_VARIANT_INACTIVE";
    throw error;
  }

  const identityKey = concreteIdentityKey(
    barcodeRecord.product_public_id,
    barcodeRecord.product_variant_public_id,
  );
  const [catalogRecord, inventoryRecord] = await Promise.all([
    getOfflineRecord(
      OFFLINE_STORES.CATALOG_ITEMS,
      identityRecordKey(
        scope,
        barcodeRecord.product_public_id,
        barcodeRecord.product_variant_public_id,
      ),
    ),
    getOfflineRecord(
      OFFLINE_STORES.INVENTORY,
      inventoryRecordKey(
        scope,
        barcodeRecord.product_public_id,
        barcodeRecord.product_variant_public_id,
      ),
    ),
  ]);
  if (!catalogRecord || catalogRecord.snapshot_id !== metadata.snapshot_id) {
    throw new Error("The scanned product is not present in the last Offline POS snapshot.");
  }

  const currentInventory = inventoryRecord?.snapshot_id === metadata.snapshot_id
    ? inventoryRecord
    : null;
  const tracked = catalogRecord.track_inventory !== false;
  const physical = tracked ? Number(currentInventory?.quantity || 0) : null;
  const reserved = tracked ? Number(currentInventory?.reserved_quantity || 0) : null;
  const pending = Number(currentInventory?.pending_offline_sold_quantity || 0);
  const available = tracked ? Math.max(0, physical - reserved - pending) : null;
  return {
    barcode: normalizedBarcode,
    barcode_public_id: barcodeRecord.barcode_public_id,
    product_public_id: catalogRecord.product_public_id,
    product_variant_public_id: catalogRecord.product_variant_public_id || null,
    variant_label: catalogRecord.variant_label || null,
    variant_options: catalogRecord.variant_options || null,
    product_name: catalogRecord.product_name,
    brand: catalogRecord.brand,
    category: catalogRecord.category,
    sku: catalogRecord.sku || null,
    price: catalogRecord.price,
    sale_claim: catalogRecord.sale_claim,
    track_inventory: tracked,
    physical_quantity: physical,
    reserved_quantity: reserved,
    available_quantity: available,
    in_stock: !tracked || available > 0,
    identity_key: identityKey,
    offline_snapshot: true,
  };
}

export function invalidateOfflineCatalogRuntimeCache(workspacePublicId, storePublicId) {
  const scope = offlineScope(workspacePublicId, storePublicId);
  if (!scope) return;
  clearSnapshotRecordCache(scope);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("offline:inventory-updated", { detail: { scope } }));
  }
}

export async function clearOfflineCatalogSnapshot(workspacePublicId, storePublicId) {
  const scope = offlineScope(workspacePublicId, storePublicId);
  if (!scope) return;
  clearSnapshotRecordCache(scope);
  await clearOfflineReadSnapshot(scope);
}
